function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { createContext, useCallback, useEffect, useContext } from 'react';
import Cookies from 'js-cookie';
import { MAX_COOKIE_SIZE, EU_CONSENT_PREFERENCES_COOKIE, DEFAULT_CONSENT_PREFERENCES_COOKIE } from './constants.js';
import useRemoveUnauthorizedCookies from './hooks/useRemoveUnauthorizedCookies.js';
import useSubscribeToCookies from './hooks/useSubscribeToCookies.js';
import { useTrackingManager } from './TrackingManagerContext.js';
import getDefaultTrackingPreference from './utils/getDefaultTrackingPreference.js';
import { getDomainWithoutSubdomain, getHostname } from './utils/getDomain.js';
import hasConsent from './utils/hasConsent.js';
import isMaxKBSize from './utils/isMaxKBSize.js';
import setGTMVariables from './utils/setGTMVariables.js';
import { jsx } from 'react/jsx-runtime';
const CookieContext = /*#__PURE__*/createContext([{}]);
function CookieProvider({
  children,
  initialValues
}) {
  const _useTrackingManager = useTrackingManager(),
    config = _useTrackingManager.config,
    region = _useTrackingManager.region,
    shadowMode = _useTrackingManager.shadowMode,
    log = _useTrackingManager.log,
    onPreferenceChange = _useTrackingManager.onPreferenceChange;
  const removeCookies = useCallback(cookies => {
    cookies.forEach(c => {
      if (!shadowMode) {
        // Attempt both .coinbase.com and www.coinbase.com since we don't know
        // which domain the value is set on
        Cookies.remove(c, {
          domain: getDomainWithoutSubdomain(),
          path: '/'
        });
        Cookies.remove(c, {
          domain: getHostname(),
          path: '/'
        });
      }
      log('Cookie does not have consent and will be removed', {
        cookie: c
      });
    });
  }, [shadowMode, log]);
  const cookieValues = useSubscribeToCookies(initialValues);
  const trackingPreference = getTrackingPreference(cookieValues, region, config);
  const adTrackingPreference = getAdTrackingPreference(cookieValues);
  useEffect(() => {
    // Need this to run before useEffect so data layer variable is set first
    setGTMVariables(trackingPreference, adTrackingPreference);
  }, []);
  useRemoveUnauthorizedCookies({
    cookieValues,
    config,
    trackingPreference,
    removeCookies
  });

  // Call preference change callback on mount to pass back initial tracking preference
  useEffect(() => {
    if (onPreferenceChange) onPreferenceChange(trackingPreference);
  }, []);
  return /*#__PURE__*/jsx(CookieContext.Provider, {
    value: cookieValues,
    children: children
  });
}
const useSetCookie = () => {
  const cookieCacheRef = useContext(CookieContext);
  const _useTrackingManager2 = useTrackingManager(),
    config = _useTrackingManager2.config,
    region = _useTrackingManager2.region,
    log = _useTrackingManager2.log,
    shadowMode = _useTrackingManager2.shadowMode,
    onError = _useTrackingManager2.onError;
  const trackingPreference = getTrackingPreference(cookieCacheRef, region, config);
  return useCallback(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (cookieName, value, options) => {
    const setCookieFunc = setCookieFunction({
      cookieName,
      trackingPreference,
      config,
      log,
      shadowMode,
      onError
    });
    setCookieFunc(value, options);
  }, [trackingPreference, config, log, shadowMode, onError]);
};
const setCookieFunction = ({
  cookieName,
  trackingPreference,
  config,
  shadowMode,
  log,
  onError
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (value, options) => {
    if (value === undefined || value === null) {
      Cookies.remove(cookieName, options);
      return;
    }
    const cookieHasConsent = hasConsent(cookieName, config, trackingPreference);
    if (cookieHasConsent || shadowMode) {
      const stringValue = JSON.stringify(value);
      const cookieSize = options?.size ?? MAX_COOKIE_SIZE;
      /*
        Url encoded cookie string (since that is what Cookies.set coverts the string into) including its name must not exceed 4KB
        For example, "," becomes %22%2C%2C making it go from 3 characters to now 9. The size has tripled.
        This is why we need to compare the url encoded stringValue instead of the stringValue itself to account for the extra characters.
      */

      if (isMaxKBSize(encodeURIComponent(stringValue) + cookieName, cookieSize)) {
        onError(new Error(`${cookieName} value exceeds ${cookieSize}KB`));
      } else {
        const newOptions = options ? _objectSpread({}, options) : undefined;
        if (newOptions?.size) {
          delete newOptions.size;
        }
        Cookies.set(cookieName, stringValue, newOptions);
      }
    }
    if (!cookieHasConsent) {
      log('Cookie does not have consent and will not be set', {
        cookie: cookieName
      });
    }
  };
};
const getTrackingPreference = (cookieCache, region, config) => {
  const trackingPreference = region === 'EU' ? cookieCache[EU_CONSENT_PREFERENCES_COOKIE] : cookieCache[DEFAULT_CONSENT_PREFERENCES_COOKIE];
  return trackingPreference || getDefaultTrackingPreference(region, config);
};
const adTrackingDefault = {
  value: 'true'
};
const getAdTrackingPreference = cookieCache => {
  const adTrackingPreference = cookieCache['advertising_sharing_allowed'];
  return adTrackingPreference || adTrackingDefault;
};
const useCookie = (cookieName
// eslint-disable-next-line @typescript-eslint/no-explicit-any
) => {
  const cookieCache = useContext(CookieContext);
  const _useTrackingManager3 = useTrackingManager(),
    config = _useTrackingManager3.config,
    region = _useTrackingManager3.region,
    log = _useTrackingManager3.log,
    shadowMode = _useTrackingManager3.shadowMode,
    onError = _useTrackingManager3.onError;
  const trackingPreference = getTrackingPreference(cookieCache, region, config);
  const setCookie = setCookieFunction({
    cookieName,
    trackingPreference,
    config,
    log,
    shadowMode,
    onError
  });
  const cookieValue = useContext(CookieContext)[cookieName];
  return [cookieValue, setCookie];
};
export { CookieProvider, useCookie, useSetCookie };
//# sourceMappingURL=CookieContext.js.map
